import type { MapPoint } from './types';
import { MAX_ZOOM } from './zoom';

/**
 * A circle **Boundary**: its **Mid Point** and its **Radius** in feet (`CONTEXT.md` § Field mapping).
 * Stored rather than derived from the ring, so a circle reopens as a circle.
 *
 * Defined here rather than in the feature's `schemas.ts` because `lib` may not import a feature
 * (`eslint-plugin-boundaries`) and this module is the one that consumes it. `schemas.ts`'s
 * `fieldCircleSchema` parses to exactly this shape.
 */
export type FieldCircle = { midPoint: MapPoint; radiusFt: number };

/** A freshly dropped **Mid Point** starts here (113601). Always inside the range below. */
export const DEFAULT_CIRCLE_RADIUS_FT = 100;

/** The inclusive **Radius** range, in feet. The backend's own `MIN_RADIUS_FT`/`MAX_RADIUS_FT`,
 *  mirrored so the two sides cannot drift. */
export const MIN_CIRCLE_RADIUS_FT = 1;
export const MAX_CIRCLE_RADIUS_FT = 26_400;

/**
 * How many decimal places a **Radius** may carry. The backend's `CircleDto` carries
 * `@IsNumber({ maxDecimalPlaces: 2 })`, so this is its rule mirrored, not a display choice — and
 * breaking it is a **422 from the validation pipe**, not a `circle.radius` verdict, exactly as an
 * out-of-range radius is (see `checkCirclePlacement`).
 *
 * It lives here, beside the range it belongs with, rather than in the feature that first needed it:
 * `radiusFromHandleDrag` below produces a Radius too, and `lib` may not import a feature.
 */
export const RADIUS_DECIMALS = 2;

/**
 * A **Radius** rounded to the precision the contract accepts — the one place {@link RADIUS_DECIMALS}
 * is applied to a *number*, as `sanitizeRadiusInput` is the one place it is applied to typed text.
 *
 * Rounded, not truncated: this takes a measured distance, where the input sanitiser takes
 * keystrokes and must not rewrite a fraction the Grower is still typing.
 */
export function roundRadiusFt(radiusFt: number): number {
  return Number(radiusFt.toFixed(RADIUS_DECIMALS));
}

/**
 * The inline message a **Radius** outside the range shows, and the toast a reverted resize shows —
 * one string for both (113601).
 *
 * **Built from the constants above**, not typed out: the sentence quotes the numbers, so a bound
 * that changes without the sentence changing is a lie the compiler cannot catch. Same idiom as
 * `use-field-points.ts`'s `maxPointsMessage`. `en-US` explicitly, so the comma is not a runtime
 * locale accident.
 */
export const RADIUS_RANGE_MESSAGE = `Enter a valid radius between ${MIN_CIRCLE_RADIUS_FT.toLocaleString(
  'en-US',
)} and ${MAX_CIRCLE_RADIUS_FT.toLocaleString('en-US')} ft.`;

/**
 * Is this a **Radius** a circle may have? The client's own answer, and it has to be: the backend's
 * `CircleDto` carries `@Min`/`@Max` on `radiusFt`, so an out-of-range radius is rejected by its
 * validation pipe as a 422 and never reaches the service that would return a `circle.radius`
 * verdict. Range is therefore one of the syntactic rules ADR-0018 assigns to the client, and an
 * out-of-range radius must never be sent.
 */
export function isRadiusInRange(value: number): boolean {
  return value >= MIN_CIRCLE_RADIUS_FT && value <= MAX_CIRCLE_RADIUS_FT;
}

/** Mean Earth radius (m), for the spherical maths below. */
const EARTH_RADIUS_M = 6_371_000;
const METERS_PER_FOOT = 0.3048;

const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

/**
 * The spherical destination-point formula: the point `distanceFt` from `origin` along the
 * great-circle `bearingDeg`.
 *
 * This is the crux of the whole module. A naive degrees-per-foot offset — the obvious thing —
 * treats a degree of longitude as a degree of latitude, so away from the equator it draws a visibly
 * squashed **ellipse** rather than a circle.
 */
function destinationPoint(origin: MapPoint, distanceFt: number, bearingDeg: number): MapPoint {
  const angularDistance = (distanceFt * METERS_PER_FOOT) / EARTH_RADIUS_M;
  const bearing = toRad(bearingDeg);
  const lat1 = toRad(origin.lat);
  const lon1 = toRad(origin.lon);

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
      Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing),
  );
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1),
      Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2),
    );

  return { lat: toDeg(lat2), lon: toDeg(lon2) };
}

const CIRCLE_RING_SEGMENTS = 64;

/**
 * A circle's Boundary ring, materialised on the sphere — **for rendering only** (grilling decision
 * 15). Placement sends the circle itself and the server samples the rim; these 64 points are never
 * put on the wire.
 *
 * Never repeats the first point as a closing coordinate: `<MapPolygon>` closes a ring of three or
 * more itself.
 */
export function circleRing(circle: FieldCircle): MapPoint[] {
  const points: MapPoint[] = [];
  for (let i = 0; i < CIRCLE_RING_SEGMENTS; i += 1) {
    points.push(
      destinationPoint(circle.midPoint, circle.radiusFt, (360 * i) / CIRCLE_RING_SEGMENTS),
    );
  }
  return points;
}

/** The bearings of the four resize handles, in the order {@link boundingSquareCorners} returns them. */
export const HANDLE_BEARINGS_DEG = [45, 135, 225, 315] as const;

/**
 * The four corners of the square that bounds the circle (Figma `598:539873` — side 2·r, the circle
 * inscribed in it), each `radiusFt·√2` from the **Mid Point** along the sphere.
 */
export function boundingSquareCorners(circle: FieldCircle): MapPoint[] {
  return HANDLE_BEARINGS_DEG.map((bearing) =>
    destinationPoint(circle.midPoint, circle.radiusFt * Math.SQRT2, bearing),
  );
}

/**
 * The **Radius** implied by a bounding-square corner dragged to `dragged`: the haversine
 * great-circle distance from the **Mid Point**, divided by `√2`.
 *
 * Which corner was dragged does not matter, and that is why the drag needs no bearing: a bounding
 * box's corner sits at `radius·√2` from the centre in every one of the four directions.
 */
export function radiusFromHandleDrag(midPoint: MapPoint, dragged: MapPoint): number {
  const dLat = toRad(dragged.lat - midPoint.lat);
  const dLon = toRad(dragged.lon - midPoint.lon);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(midPoint.lat)) * Math.cos(toRad(dragged.lat)) * Math.sin(dLon / 2) ** 2;
  const angularDistance = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  // Rounded, because what comes back is a Radius and a Radius carries two decimals. Raw, the
  // great-circle maths yields ~14 of them, which `CircleDto` rejects as a 422 — so before this every
  // handle drag failed its placement check with "We couldn't check that location", the one refusal
  // that means the round trip never happened. Sub-micron precision on a distance in feet was never
  // worth anything anyway.
  return roundRadiusFt((angularDistance * EARTH_RADIUS_M) / METERS_PER_FOOT / Math.SQRT2);
}

/**
 * The **Mid Point** marker is a 21px dot (`marked-points.module.scss`), so a circle whose projected
 * radius is under about 11px is drawn entirely underneath it — ring, bounding square and all four
 * corner handles — and reads as "nothing appeared". The Field Mapping screen opens at
 * `STANDARD_ZOOM`, where a foot is worth roughly a twelfth of a pixel: the
 * {@link DEFAULT_CIRCLE_RADIUS_FT} disc lands at 8px with its handles 11px out, i.e. invisible and
 * ungrabbable.
 *
 * 113601 fixes the default radius at 100 ft — twice, verbatim — so the radius is not what may change.
 * The camera is. Measured from Figma `598:538815`, whose bounding square is 156px across on a 1440px
 * frame: the frame draws a freshly placed circle at a **78px radius**, which is what makes it both
 * legible and grabbable.
 */
export const CIRCLE_ARRIVAL_RADIUS_PX = 78;

/** Web Mercator ground resolution at zoom 0, latitude 0, for a 256px tile (m/px) — the constant
 *  Google's own projection uses. Distinct from {@link EARTH_RADIUS_M} above, which serves the
 *  great-circle maths; this one serves the screen. */
const EQUATOR_M_PER_PX_AT_ZOOM_0 = 156_543.033_928;

/**
 * The zoom the camera arrives at when a **Mid Point** is placed, so the circle around it is drawn at
 * {@link CIRCLE_ARRIVAL_RADIUS_PX} rather than under its own marker.
 *
 * **A floor at the current zoom, never a ceiling.** A Grower already working closer in than this is
 * not pulled back out — the shape is legible there already, and yanking the camera outward would be
 * the more surprising of the two. So this only ever zooms *in*, and returns `currentZoom` unchanged
 * when nothing needs to happen, which is how the page decides whether to move the camera at all.
 *
 * Capped at {@link MAX_ZOOM} — the same ceiling `<Map maxZoom>` and the zoom controls enforce, so
 * this can never command a zoom the map refuses. A 1 ft radius wants far more than 20 and simply
 * gets 20; a 26,400 ft radius wants about 10 and, being below the floor, gets the current zoom.
 *
 * Latitude matters and is read from the **Mid Point**: Mercator's scale is `1/cos(latitude)`, so the
 * same radius in feet is worth two-thirds as many pixels in Kansas as it is at the equator. Ignoring
 * it would leave the northern half of the USA short of the zoom it asked for.
 *
 * Same shape as `deviceArrivalZoom` in `./search-camera.ts`, and for the same reason: a camera
 * rule with a floor and a ceiling is a pure function the page hands to its adapter, not a branch
 * buried in a click handler.
 */
export function circleArrivalZoom(circle: FieldCircle, currentZoom: number): number {
  const radiusM = circle.radiusFt * METERS_PER_FOOT;
  // A zero or negative radius has no zoom that renders it; leave the camera where it is rather than
  // returning ±Infinity from the logarithm below.
  if (radiusM <= 0) return currentZoom;

  const latitudeScale = Math.cos(toRad(circle.midPoint.lat));
  const ideal = Math.log2(
    (CIRCLE_ARRIVAL_RADIUS_PX * EQUATOR_M_PER_PX_AT_ZOOM_0 * latitudeScale) / radiusM,
  );

  return Math.min(MAX_ZOOM, Math.max(currentZoom, ideal));
}
